import { useState } from 'react'
import dayjs from 'dayjs'
import { useDebouncedCallback } from 'use-debounce'
import { t, Trans } from '@lingui/macro'
import { useLingui } from '@lingui/react'
import List from '@mui/material/List'
import ListItemButton from '@mui/material/ListItemButton'
import ListItemText from '@mui/material/ListItemText'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'

import { PatientSourceEnum } from '@/core/enums/patient-source.enum'
import { useSearchPatientsQuery } from '@/core/services/patient'
import { SelectedPatient } from '../model/useSimplifiedReception'

type ExistingPatientStepProps = { onSelect: (patient: SelectedPatient) => void }

/** Search field + result list to pick an existing patient. */
export function ExistingPatientStep({ onSelect }: ExistingPatientStepProps) {
  useLingui()
  const [search, setSearch] = useState<string>()
  const onSearch = useDebouncedCallback((value: string) => setSearch(value.trim() || undefined), 300)
  const { data, isFetching } = useSearchPatientsQuery({ patientSource: PatientSourceEnum.MedconnectPatient, smartSearch: search, limit: 10 })

  return (
    <Stack spacing={2}>
      <TextField autoFocus fullWidth label={t`Search a patient (name, birth date, social security number)`} onChange={(event) => onSearch(event.target.value)} />
      <List aria-label={t`Patients found`}>
        {data?.items.map((patient) => {
          const label = `${patient.userInfo.name} ${patient.userInfo.surname}`
          const birthday = patient.userInfo.birthday ? dayjs(patient.userInfo.birthday).format('DD/MM/YYYY') : ''
          return (
            <ListItemButton key={patient._id} divider onClick={() => onSelect({ id: patient._id, label })}>
              <ListItemText primary={label} secondary={birthday} />
            </ListItemButton>
          )
        })}
      </List>
      {!isFetching && data?.items.length === 0 && (
        <Typography color="text.secondary"><Trans>No patient found</Trans></Typography>
      )}
    </Stack>
  )
}
